'use client';

import { useState } from 'react';
import ServiceHero from './ServiceHero';
import ServiceCard from './ServiceCard';
import ServiceComparison from './ServiceComparison';
import PlantHireSection from './PlantHireSection';
import ProcessTimeline from './ProcessTimeline';
import ServiceCTA from './ServiceCTA';
import Icon from '../../../components/ui/AppIcon';
import type { SiteSettings } from '../../../lib/sanity/types';

interface Service {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  icon: string;
  features: Array<{ text: string }>;
  colorScheme: 'primary' | 'accent';
  category: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
}

interface ServicesInteractiveProps {
  hero: { title: string; description: string; stats: { value: string; label: string; icon: string }[]; heroImage?: string; heroImageAlt?: string };
  categories: Category[];
  services: Service[];
  comparisonPackages: any[];
  comparisonFeatures: any[];
  equipment: any[];
  processSteps: any[];
  plantHire: {
    title?: string;
    description?: string;
    partnershipTitle?: string;
    partnershipSubtitle?: string;
    partnershipDescription?: string;
    clients?: string[];
    featureBadges?: { label: string; icon: string }[];
    image?: string;
    imageAlt?: string;
  };
  cta: { badge?: string; title?: string; description?: string; benefits?: string[]; image?: string; imageAlt?: string };
  settings?: SiteSettings | null;
}

export default function ServicesInteractive({
  hero,
  categories,
  services,
  comparisonPackages,
  comparisonFeatures,
  equipment,
  processSteps,
  plantHire,
  cta,
  settings,
}: ServicesInteractiveProps) {
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const allCategories: Category[] = [{ id: 'all', name: 'All Services', icon: 'Squares2X2Icon' }, ...categories];

  const filteredServices = activeCategory === 'all' ? services : services.filter((service) => service.category === activeCategory);

  return (
    <div data-component-id="app/services/components/ServicesInteractive.tsx:72:4" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="72" data-component-end-line="126" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <ServiceHero data-component-id="app/services/components/ServicesInteractive.tsx:73:6" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="73" data-component-end-line="73" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceHero" data-component-content="%7B%22elementName%22%3A%22ServiceHero%22%7D" title={hero.title} description={hero.description} stats={hero.stats} heroImage={hero.heroImage} heroImageAlt={hero.heroImageAlt} />

      {/* Category Filter */}
      {allCategories.length > 1 && (
        <section data-component-id="app/services/components/ServicesInteractive.tsx:77:8" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="77" data-component-end-line="96" data-component-file="ServicesInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-12%20bg-muted%20border-b%20border-border%22%7D" className="py-12 bg-muted border-b border-border">
          <div data-component-id="app/services/components/ServicesInteractive.tsx:78:10" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="78" data-component-end-line="95" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%22%7D" className="container mx-auto px-4">
            <div data-component-id="app/services/components/ServicesInteractive.tsx:79:12" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="79" data-component-end-line="94" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20justify-center%20gap-3%22%7D" className="flex flex-wrap justify-center gap-3">
              {allCategories.map((category) => (
                <button data-component-id="app/services/components/ServicesInteractive.tsx:81:16" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="81" data-component-end-line="92" data-component-file="ServicesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
                    activeCategory === category.id
                      ? 'bg-primary text-primary-foreground shadow-construction'
                      : 'bg-card text-foreground hover:bg-primary hover:text-primary-foreground'
                  }`}
                >
                  <Icon data-component-id="app/services/components/ServicesInteractive.tsx:90:18" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="90" data-component-end-line="90" data-component-file="ServicesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={category.icon as any} size={20} />
                  <span data-component-id="app/services/components/ServicesInteractive.tsx:91:18" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="91" data-component-end-line="91" data-component-file="ServicesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{category.name}</span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Grid */}
      <section data-component-id="app/services/components/ServicesInteractive.tsx:100:6" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="100" data-component-end-line="117" data-component-file="ServicesInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-20%20bg-background%22%7D" className="py-20 bg-background">
        <div data-component-id="app/services/components/ServicesInteractive.tsx:101:8" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="101" data-component-end-line="116" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%22%7D" className="container mx-auto px-4">
          <div data-component-id="app/services/components/ServicesInteractive.tsx:102:10" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="102" data-component-end-line="115" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.map((service, index) => (
              <ServiceCard data-component-id="app/services/components/ServicesInteractive.tsx:104:14" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="104" data-component-end-line="113" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceCard" data-component-content="%7B%22elementName%22%3A%22ServiceCard%22%7D"
                key={index}
                title={service.title}
                description={service.description}
                image={service.image}
                imageAlt={service.imageAlt}
                icon={service.icon}
                features={service.features}
                colorScheme={service.colorScheme}
              />
            ))}
          </div>
        </div>
      </section>

      <ServiceComparison data-component-id="app/services/components/ServicesInteractive.tsx:119:6" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="119" data-component-end-line="119" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceComparison" data-component-content="%7B%22elementName%22%3A%22ServiceComparison%22%7D" features={comparisonFeatures} packages={comparisonPackages} />

      <PlantHireSection data-component-id="app/services/components/ServicesInteractive.tsx:121:6" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="121" data-component-end-line="121" data-component-file="ServicesInteractive.tsx" data-component-name="PlantHireSection" data-component-content="%7B%22elementName%22%3A%22PlantHireSection%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" equipment={equipment} {...plantHire} />

      <ProcessTimeline data-component-id="app/services/components/ServicesInteractive.tsx:123:6" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="123" data-component-end-line="123" data-component-file="ServicesInteractive.tsx" data-component-name="ProcessTimeline" data-component-content="%7B%22elementName%22%3A%22ProcessTimeline%22%7D" steps={processSteps} />

      <ServiceCTA data-component-id="app/services/components/ServicesInteractive.tsx:125:6" data-component-path="app/services/components/ServicesInteractive.tsx" data-component-line="125" data-component-end-line="125" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceCTA" data-component-content="%7B%22elementName%22%3A%22ServiceCTA%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...cta} settings={settings} />
    </div>
  );
}
